/**
 * Shared allocation generation utility with dependency-aware scheduling.
 * Used by both ProjectDetail (Generate button) and Admin delivery profile editor.
 *
 * Implements Critical Path Method (CPM):
 * 1. Build dependency graph (explicit predecessors + implicit phase ordering)
 * 2. Forward pass: earliest start/finish
 * 3. Backward pass: latest start/finish, float
 * 4. Schedule with resource constraints (role+skill) respecting dependencies
 */

import type { ActivityPhase } from '@/hooks/useWorkstreams';

// Phase execution order
const PHASE_ORDER: ActivityPhase[] = [
  'workshop', 'assess', 'design', 'implement', 'validate', 'document', 'kt',
];

export interface ActivityDependencyInput {
  predecessorId: string;
  type: 'FS' | 'SS' | 'FF';
  lagDays: number;
}

export interface GenerateActivity {
  id: string;
  role: string;
  skill: string;
  adjustedDays: number;
  phase?: ActivityPhase | null;
  sortOrder?: number;
  predecessors?: ActivityDependencyInput[];
}

export interface ScheduleResult {
  allocations: Record<string, Record<number, number>>;
  criticalPath: Set<string>;
  earliestStart: Record<string, number>;
  earliestFinish: Record<string, number>;
  latestStart: Record<string, number>;
  latestFinish: Record<string, number>;
  float: Record<string, number>;
}

/**
 * Build the full dependency graph: explicit predecessors + implicit phase ordering.
 * Within a capability, activities in the previous phase are implicit FS predecessors
 * of activities in the next phase (when no explicit predecessors exist).
 */
function buildDependencyGraph(
  activities: GenerateActivity[],
): Map<string, Array<{ predecessorId: string; type: 'FS' | 'SS' | 'FF'; lagDays: number }>> {
  const graph = new Map<string, Array<{ predecessorId: string; type: 'FS' | 'SS' | 'FF'; lagDays: number }>>();

  for (const act of activities) {
    graph.set(act.id, [...(act.predecessors || [])]);
  }

  // Add implicit phase-based dependencies for activities without explicit predecessors
  const byPhase = new Map<string, GenerateActivity[]>();
  for (const act of activities) {
    if (!act.phase) continue;
    if (!byPhase.has(act.phase)) byPhase.set(act.phase, []);
    byPhase.get(act.phase)!.push(act);
  }

  for (let i = 1; i < PHASE_ORDER.length; i++) {
    const prevPhase = PHASE_ORDER[i - 1];
    const currPhase = PHASE_ORDER[i];
    const prevActivities = byPhase.get(prevPhase) || [];
    const currActivities = byPhase.get(currPhase) || [];

    if (prevActivities.length === 0) continue;

    for (const curr of currActivities) {
      const deps = graph.get(curr.id) || [];
      // Only add implicit dependencies if this activity has no explicit predecessors
      if (deps.length === 0) {
        // All activities in previous phase are implicit FS predecessors
        for (const prev of prevActivities) {
          deps.push({ predecessorId: prev.id, type: 'FS', lagDays: 0 });
        }
        graph.set(curr.id, deps);
      }
    }
  }

  return graph;
}

/**
 * Calculate how many weeks an activity takes given days per week.
 * Returns fractional weeks (e.g., 1 day at 5d/week = 0.2 weeks) so that
 * FS-dependent activities with the same resource can pack into the same week.
 */
function weeksNeeded(days: number, daysPerWeek: number): number {
  if (days <= 0) return 0;
  return days / daysPerWeek;
}

/**
 * Forward pass: calculate earliest start and finish for each activity.
 * Uses topological sort order.
 */
function forwardPass(
  activities: GenerateActivity[],
  graph: Map<string, Array<{ predecessorId: string; type: 'FS' | 'SS' | 'FF'; lagDays: number }>>,
  startWeek: number,
  daysPerWeek: number,
): { earliestStart: Record<string, number>; earliestFinish: Record<string, number> } {
  const earliestStart: Record<string, number> = {};
  const earliestFinish: Record<string, number> = {};
  const actMap = new Map(activities.map(a => [a.id, a]));

  // Topological sort (Kahn's algorithm)
  const inDegree: Record<string, number> = {};
  for (const act of activities) inDegree[act.id] = 0;
  for (const [actId, deps] of graph) {
    inDegree[actId] = (inDegree[actId] || 0); // ensure exists
    for (const dep of deps) {
      if (actMap.has(dep.predecessorId)) {
        inDegree[actId]++;
      }
    }
  }

  const queue: string[] = [];
  for (const act of activities) {
    if (inDegree[act.id] === 0) queue.push(act.id);
  }

  const sorted: string[] = [];
  while (queue.length > 0) {
    // Sort by phase order then sort_order for deterministic ordering
    queue.sort((a, b) => {
      const actA = actMap.get(a)!;
      const actB = actMap.get(b)!;
      const phaseA = actA.phase ? PHASE_ORDER.indexOf(actA.phase) : -1;
      const phaseB = actB.phase ? PHASE_ORDER.indexOf(actB.phase) : -1;
      if (phaseA !== phaseB) return phaseA - phaseB;
      return (actA.sortOrder || 0) - (actB.sortOrder || 0);
    });

    const id = queue.shift()!;
    sorted.push(id);

    // Decrease in-degree for successors
    for (const [succId, deps] of graph) {
      for (const dep of deps) {
        if (dep.predecessorId === id) {
          inDegree[succId]--;
          if (inDegree[succId] === 0) queue.push(succId);
        }
      }
    }
  }

  // Calculate earliest start/finish
  for (const id of sorted) {
    const act = actMap.get(id)!;
    const deps = graph.get(id) || [];
    let es = startWeek;

    for (const dep of deps) {
      if (!actMap.has(dep.predecessorId)) continue;
      const lagWeeks = Math.ceil(dep.lagDays / daysPerWeek);

      if (dep.type === 'FS') {
        const predFinish = earliestFinish[dep.predecessorId] || startWeek;
        es = Math.max(es, predFinish + lagWeeks);
      } else if (dep.type === 'SS') {
        const predStart = earliestStart[dep.predecessorId] || startWeek;
        es = Math.max(es, predStart + lagWeeks);
      } else if (dep.type === 'FF') {
        // B must finish when A finishes (+ lag): B's earliest start = A's finish - B's duration
        const predFinish = earliestFinish[dep.predecessorId] || startWeek;
        const myDuration = weeksNeeded(act.adjustedDays, daysPerWeek);
        es = Math.max(es, predFinish + lagWeeks - myDuration);
      }
    }

    earliestStart[id] = es;
    earliestFinish[id] = es + weeksNeeded(act.adjustedDays, daysPerWeek);
  }

  return { earliestStart, earliestFinish };
}

/**
 * Backward pass: calculate latest start/finish and float.
 */
function backwardPass(
  activities: GenerateActivity[],
  graph: Map<string, Array<{ predecessorId: string; type: 'FS' | 'SS' | 'FF'; lagDays: number }>>,
  earliestStart: Record<string, number>,
  earliestFinish: Record<string, number>,
  daysPerWeek: number,
): { latestStart: Record<string, number>; latestFinish: Record<string, number>; float: Record<string, number>; criticalPath: Set<string> } {
  // Find project end (max earliest finish)
  const projectEnd = Math.max(...Object.values(earliestFinish), 1);

  const latestFinish: Record<string, number> = {};
  const latestStart: Record<string, number> = {};
  const float: Record<string, number> = {};
  const actMap = new Map(activities.map(a => [a.id, a]));

  // Build reverse graph (successors)
  const successors = new Map<string, Array<{ successorId: string; type: 'FS' | 'SS' | 'FF'; lagDays: number }>>();
  for (const [actId, deps] of graph) {
    for (const dep of deps) {
      if (!actMap.has(dep.predecessorId)) continue;
      if (!successors.has(dep.predecessorId)) successors.set(dep.predecessorId, []);
      successors.get(dep.predecessorId)!.push({ successorId: actId, type: dep.type, lagDays: dep.lagDays });
    }
  }

  // Process in reverse topological order
  const sorted = [...activities].sort((a, b) => (earliestFinish[b.id] || 0) - (earliestFinish[a.id] || 0));

  for (const act of sorted) {
    const succs = successors.get(act.id) || [];
    let lf = projectEnd;

    for (const succ of succs) {
      const lagWeeks = Math.ceil(succ.lagDays / daysPerWeek);
      if (succ.type === 'FS') {
        lf = Math.min(lf, (latestStart[succ.successorId] || projectEnd) - lagWeeks);
      } else if (succ.type === 'SS') {
        lf = Math.min(lf, (latestStart[succ.successorId] || projectEnd) - lagWeeks + weeksNeeded(act.adjustedDays, daysPerWeek));
      } else if (succ.type === 'FF') {
        // B finishes when A finishes (+ lag): A's latestFinish = B's latestFinish - lag
        lf = Math.min(lf, (latestFinish[succ.successorId] || projectEnd) - lagWeeks);
      }
    }

    latestFinish[act.id] = lf;
    latestStart[act.id] = lf - weeksNeeded(act.adjustedDays, daysPerWeek);
    float[act.id] = latestStart[act.id] - earliestStart[act.id];
  }

  const criticalPath = new Set<string>();
  for (const act of activities) {
    if (Math.abs(float[act.id]) < 0.001) {
      criticalPath.add(act.id);
    }
  }

  return { latestStart, latestFinish, float, criticalPath };
}

/**
 * Generate week-by-week allocations with dependency-aware scheduling.
 *
 * Algorithm:
 * 1. Build dependency graph (explicit + implicit phase-based)
 * 2. CPM forward pass → earliest start per activity
 * 3. Schedule activities respecting both dependencies and resource constraints
 * 4. CPM backward pass → critical path identification
 */
export function generateWeekAllocations(
  activities: GenerateActivity[],
  startWeek: number,
  daysPerWeek = 5,
  numWeeks = 44,
): ScheduleResult {
  if (activities.length === 0) {
    return { allocations: {}, criticalPath: new Set(), earliestStart: {}, earliestFinish: {}, latestStart: {}, latestFinish: {}, float: {} };
  }

  // 1. Build dependency graph
  const graph = buildDependencyGraph(activities);

  // 2. Forward pass — earliest start/finish
  const { earliestStart, earliestFinish } = forwardPass(activities, graph, startWeek, daysPerWeek);

  // 3. Schedule with resource constraints, respecting earliest start
  const resourceState: Record<string, { week: number; remaining: number }> = {};
  const allocations: Record<string, Record<number, number>> = {};

  // Sort by earliest start, then phase, then sort order
  const sortedActivities = [...activities].sort((a, b) => {
    const esA = earliestStart[a.id] || startWeek;
    const esB = earliestStart[b.id] || startWeek;
    if (esA !== esB) return esA - esB;
    const phaseA = a.phase ? PHASE_ORDER.indexOf(a.phase) : -1;
    const phaseB = b.phase ? PHASE_ORDER.indexOf(b.phase) : -1;
    if (phaseA !== phaseB) return phaseA - phaseB;
    return (a.sortOrder || 0) - (b.sortOrder || 0);
  });

  for (const act of sortedActivities) {
    if (act.adjustedDays <= 0) {
      allocations[act.id] = {};
      continue;
    }

    const resourceKey = `${act.role}|${act.skill}`;
    if (!resourceState[resourceKey]) {
      resourceState[resourceKey] = { week: startWeek, remaining: daysPerWeek };
    }
    const state = resourceState[resourceKey];

    // Resource can't start before its earliest start from dependencies.
    // esRaw is fractional (e.g., 1.6 = 60% through week 1 = day 3 of 5).
    // The resource can only use the REMAINING days in that week.
    const esRaw = earliestStart[act.id] || startWeek;
    const esWeek = Math.max(startWeek, Math.floor(esRaw));
    const fractionalPart = esRaw - Math.floor(esRaw); // e.g., 0.6 for es=1.6
    const daysConsumedInWeek = fractionalPart * daysPerWeek; // e.g., 0.6 * 5 = 3 days already used
    const remainingInEsWeek = daysPerWeek - daysConsumedInWeek; // e.g., 5 - 3 = 2 days available

    if (state.week < esWeek) {
      state.week = esWeek;
      state.remaining = remainingInEsWeek;
    } else if (state.week === esWeek && state.remaining > remainingInEsWeek) {
      // Same week but dependency constrains when we can start — cap remaining
      state.remaining = Math.min(state.remaining, remainingInEsWeek);
    }

    const weekAllocations: Record<number, number> = {};
    let toAllocate = act.adjustedDays;

    while (toAllocate > 0.001 && state.week <= numWeeks) {
      const alloc = Math.min(toAllocate, state.remaining);
      const rounded = Math.round(alloc * 10) / 10;
      if (rounded > 0) {
        weekAllocations[state.week] = (weekAllocations[state.week] || 0) + rounded;
      }
      toAllocate -= alloc;
      state.remaining -= alloc;

      if (state.remaining < 0.001) {
        state.week++;
        state.remaining = daysPerWeek;
      }
    }

    allocations[act.id] = weekAllocations;
  }

  // 4. Backward pass — critical path
  const { latestStart, latestFinish, float, criticalPath } = backwardPass(
    activities, graph, earliestStart, earliestFinish, daysPerWeek
  );

  return { allocations, criticalPath, earliestStart, earliestFinish, latestStart, latestFinish, float };
}

/**
 * Check if any named resource is over-allocated in a given week.
 */
export function isWeekOverAllocated(
  weekResourceTotals: Record<string, number>,
  daysPerWeek: number,
): boolean {
  return Object.values(weekResourceTotals).some(days => days > daysPerWeek);
}

/**
 * Compute per-resource totals for each week across all activities.
 */
export function computeWeekResourceTotals(
  activities: Array<{ id: string; role: string; skill: string; isExcluded?: boolean }>,
  allocations: Record<string, { weekAllocations: Record<number, number> }>,
): Record<number, Record<string, number>> {
  const result: Record<number, Record<string, number>> = {};

  for (const act of activities) {
    if (act.isExcluded) continue;
    const alloc = allocations[act.id];
    if (!alloc) continue;
    const resourceKey = `${act.role}|${act.skill}`;

    for (const [weekStr, days] of Object.entries(alloc.weekAllocations)) {
      const week = Number(weekStr);
      if (!result[week]) result[week] = {};
      result[week][resourceKey] = (result[week][resourceKey] || 0) + days;
    }
  }

  return result;
}

/**
 * Check if an activity's allocations violate any dependency constraints.
 * Returns the violated predecessor info, or null if no violation.
 */
export function checkDependencyViolation(
  activityId: string,
  allocations: Record<string, Record<number, number>>,
  predecessors: Array<{ predecessorId: string; type: 'FS' | 'SS' | 'FF'; lagDays: number }>,
  daysPerWeek: number,
): { predecessorId: string; message: string } | null {
  const actWeeks = Object.keys(allocations[activityId] || {}).map(Number).sort((a, b) => a - b);
  if (actWeeks.length === 0) return null;
  const actStart = actWeeks[0];
  const actFinish = actWeeks[actWeeks.length - 1];

  for (const dep of predecessors) {
    const predWeeks = Object.keys(allocations[dep.predecessorId] || {}).map(Number).sort((a, b) => a - b);
    if (predWeeks.length === 0) continue;

    const lagWeeks = Math.ceil(dep.lagDays / daysPerWeek);

    if (dep.type === 'FS') {
      const predFinish = predWeeks[predWeeks.length - 1] + 1;
      if (actStart < predFinish + lagWeeks) {
        return { predecessorId: dep.predecessorId, message: `Starts before predecessor finishes (W${predFinish + lagWeeks})` };
      }
    } else if (dep.type === 'SS') {
      const predStart = predWeeks[0];
      if (actStart < predStart + lagWeeks) {
        return { predecessorId: dep.predecessorId, message: `Starts before predecessor starts (W${predStart + lagWeeks})` };
      }
    } else if (dep.type === 'FF') {
      const predFinish = predWeeks[predWeeks.length - 1];
      if (actFinish < predFinish + lagWeeks) {
        return { predecessorId: dep.predecessorId, message: `Finishes before predecessor finishes (W${predFinish + lagWeeks})` };
      }
    }
  }

  return null;
}
